<template>
  <Page>
    <div class="flex flex-col gap-2 flex-1 min-h-0">
      <div class="bg-background-card rounded-lg shadow-card p-3 border border-border border-l-4 border-l-primary hover:shadow-card-hover transition-shadow shrink-0">
        <div class="flex items-center gap-3 min-w-0">
          <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
            <i class="fa-solid fa-folder-tree text-primary text-sm"></i>
          </div>
          <div class="min-w-0">
            <h1 class="text-sm font-bold text-text flex items-center gap-2 flex-wrap">
              Select a Project
              <span
                v-if="totalProjects > 0"
                class="inline-flex items-center h-5 px-2 rounded-full bg-primary/10 text-primary text-[0.68rem] font-bold tracking-wide"
              >
                {{ totalProjects.toLocaleString() }} projects
              </span>
            </h1>
            <p :title="`Welcome, ${userName}. Select a project to continue.`" class="text-xs text-text-muted truncate">Welcome, {{ userName }}. Select a project to continue.</p>
          </div>
        </div>
      </div>

      <div class="bg-background-card rounded-lg shadow-card p-3 border border-border hover:shadow-card-hover transition-shadow shrink-0">
        <div class="flex items-center gap-2">
          <div class="relative flex-1 max-w-120">
            <i class="fa-solid fa-magnifying-glass absolute left-3 top-1/2 -translate-y-1/2 text-secondary text-xs pointer-events-none"></i>
            <InputText
              v-model="search"
              placeholder="Search projects…"
              class="w-full pl-9"
              size="small"
            />
          </div>
          <span v-if="search.trim() !== ''" class="hidden sm:inline text-xs text-text-muted shrink-0">
            {{ totalProjects }} result{{ totalProjects === 1 ? '' : 's' }}
          </span>
          <button
            v-if="search.trim() !== ''"
            type="button"
            class="inline-flex items-center gap-1 h-7 px-2.5 rounded-full text-xs font-semibold border border-border text-text-muted hover:border-primary/30 hover:text-text transition-colors shrink-0"
            @click="search = ''"
          >
            <i class="fa-solid fa-xmark text-[0.65rem]"></i>
            Clear
          </button>
        </div>

        <div v-if="showRecentProjects" id="recent-projects" class="mt-3 pt-3 border-t border-border-light">
          <div class="flex items-center gap-2 mb-2">
            <i class="fa-solid fa-clock-rotate-left text-accent text-[0.65rem]"></i>
            <h3 class="text-[0.68rem] font-bold uppercase tracking-widest text-text-muted">Recent projects</h3>
          </div>
          <div class="grid grid-cols-1 sm:grid-cols-3 gap-2.5">
            <button
              v-for="project in recentProjects"
              :key="`recent-${project.id}`"
              :id="`recent-project-${project.id}`"
              type="button"
              :class="[
                'group text-left bg-background border rounded-lg p-2.5 flex items-center gap-3 hover:shadow-card-hover transition-all overflow-hidden relative focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30',
                project.id === selectedProjectId ? 'border-primary bg-primary/5 shadow-md' : 'border-border-light hover:border-primary/40',
              ]"
              @click="project.id && selectProject(project.id)"
            >
              <span :class="['absolute left-0 top-0 bottom-0 w-0.5', project.status === 'Active' ? 'bg-success' : 'bg-secondary']"></span>
              <div class="w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0 ml-0.5">
                <i :class="project.icon + ' text-primary text-sm'"></i>
              </div>
              <div class="min-w-0 flex-1">
                <div class="text-sm font-bold text-text truncate pr-1">{{ project.name }}</div>
                <div class="text-xs text-text-muted truncate">{{ project.description || 'No description' }}</div>
              </div>
              <i class="fa-solid fa-arrow-right text-text-muted/40 group-hover:text-primary text-xs shrink-0 hidden sm:block"></i>
            </button>
          </div>
        </div>
      </div>

      <div id="project-selection" class="bg-background-card rounded-lg shadow-card border border-border hover:shadow-card-hover transition-shadow flex-1 min-h-0 min-h-48 flex flex-col overflow-hidden">
        <div class="flex items-center justify-between px-3 py-2 border-b border-border shrink-0 bg-background-subtle/40">
          <div class="flex items-center gap-2 min-w-0">
            <i class="fa-solid fa-layer-group text-primary text-xs shrink-0"></i>
            <span class="text-xs font-bold uppercase tracking-wide text-text shrink-0">Available Projects</span>
          </div>
          <div class="flex items-center gap-2 shrink-0">
            <button
              type="button"
              class="w-7 h-7 rounded-full bg-background border border-border text-text-muted flex items-center justify-center hover:border-primary/30 hover:text-primary transition-colors disabled:opacity-50"
              :disabled="isLoading"
              title="Refresh"
              @click="refreshCurrentPage()"
            >
              <i class="fa-solid fa-rotate text-[0.7rem]" :class="{ 'fa-spin': isLoading }"></i>
            </button>
          </div>
        </div>

        <div id="project-list" class="flex-1 min-h-0 overflow-y-auto p-2 space-y-1.5">
          <template v-if="isLoading">
            <div v-for="n in 6" :key="n" class="border border-border-light rounded-lg py-2.5 px-3 flex items-center gap-3 animate-pulse bg-background">
              <div class="w-9 h-9 rounded-lg bg-border/40 shrink-0"></div>
              <div class="flex-1 min-w-0 space-y-1.5">
                <div class="h-3 bg-border/60 rounded w-40"></div>
                <div class="h-2.5 bg-border/30 rounded w-56"></div>
              </div>
              <div class="shrink-0 w-[72px] sm:w-[84px] flex justify-center">
                <div class="h-5 w-16 bg-border/40 rounded-full"></div>
              </div>
              <div class="hidden sm:flex items-center gap-2 shrink-0 w-[176px] justify-end">
                <div class="w-6 h-6 rounded-full bg-border/40"></div>
                <div class="w-6 h-6 rounded-full bg-border/40 -ml-1"></div>
                <div class="h-3 w-20 bg-border/30 rounded hidden xl:block"></div>
              </div>
              <div class="hidden sm:block h-7 w-[68px] bg-border/30 rounded-full shrink-0"></div>
            </div>
          </template>

          <template v-else>
            <div
              v-if="projects.length === 0 && search.trim() !== '' && !isError"
              class="flex flex-col items-center justify-center text-center py-8 px-6"
            >
              <div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <i class="fa-solid fa-magnifying-glass text-primary/60"></i>
              </div>
              <h3 class="text-sm font-bold text-text">No projects found</h3>
              <p class="text-xs text-text-muted mt-1 max-w-sm">No projects match “{{ search }}”. Try a different keyword or clear the search.</p>
              <button
                type="button"
                class="mt-3 inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                @click="search = ''"
              >
                Clear search
              </button>
            </div>

            <div
              v-else-if="isError"
              class="flex flex-col items-center justify-center text-center py-8 px-6"
            >
              <div class="w-12 h-12 rounded-full bg-error/10 flex items-center justify-center mb-3">
                <i class="fa-solid fa-circle-exclamation text-error/70"></i>
              </div>
              <h3 class="text-sm font-bold text-text">Unable to load projects</h3>
              <p class="text-xs text-text-muted mt-1 max-w-sm">{{ errorMessage }}</p>
              <button
                type="button"
                class="mt-3 inline-flex items-center gap-1.5 h-7 px-3 rounded-full text-xs font-semibold border border-primary/30 text-primary hover:bg-primary/10 transition-colors"
                @click="refreshCurrentPage()"
              >
                Retry
              </button>
            </div>

            <div
              v-else-if="projects.length === 0"
              class="flex flex-col items-center justify-center text-center py-8 px-6"
            >
              <div class="w-12 h-12 rounded-full bg-primary/10 flex items-center justify-center mb-3">
                <i class="fa-solid fa-folder-open text-primary/60"></i>
              </div>
              <h3 class="text-sm font-bold text-text">No projects available</h3>
              <p class="text-xs text-text-muted mt-1">Projects will appear here once they are created for your account.</p>
            </div>

            <button
              v-for="(project, idx) in projects"
              :key="project.id"
              :id="`project-item-${idx + 1}`"
              type="button"
              :aria-current="project.id === selectedProjectId ? 'true' : undefined"
              :class="[
                'group w-full text-left relative grid grid-cols-[1fr_auto] sm:grid-cols-[1fr_84px_176px_72px] items-center gap-x-3 gap-y-1 py-2.5 px-3 rounded-lg border bg-background hover:shadow-card-hover transition-all overflow-hidden focus:outline-hidden focus-visible:ring-2 focus-visible:ring-primary/30',
                project.id === selectedProjectId ? 'border-primary bg-primary/4 shadow-xs' : 'border-border-light hover:border-primary/35',
              ]"
              @click="project.id && selectProject(project.id)"
            >
              <span :class="['absolute left-0 top-0 bottom-0 w-0.5', project.status === 'Active' ? 'bg-success' : 'bg-secondary']" aria-hidden="true"></span>

              <div class="flex items-center gap-3 min-w-0">
                <div class="relative w-9 h-9 rounded-lg bg-primary/10 flex items-center justify-center shrink-0">
                  <i :class="project.icon + ' text-primary text-sm'"></i>
                  <span
                    v-if="project.id === selectedProjectId"
                    class="absolute -top-1 -right-1 w-4 h-4 bg-success rounded-full flex items-center justify-center ring-2 ring-background"
                  >
                    <i class="fa-solid fa-check text-white text-[0.5rem]"></i>
                  </span>
                </div>
                <div class="min-w-0">
                  <div :title="project.name" :class="['truncate text-sm font-semibold leading-none', project.id === selectedProjectId ? 'text-primary' : 'text-text']">
                    {{ project.name }}
                  </div>
                  <p class="text-xs text-text-muted truncate leading-tight mt-0.5">{{ project.description || 'No description' }}</p>
                </div>
              </div>

              <div class="hidden sm:flex justify-center shrink-0">
                <span
                  :class="[
                    'inline-flex items-center justify-center gap-1 h-5 px-2 rounded-full text-[0.65rem] font-bold border w-fit',
                    project.status === 'Active' ? 'bg-success/10 border-success/20 text-success' : 'bg-secondary/10 border-secondary/20 text-secondary',
                  ]"
                >
                  <span :class="['w-1.5 h-1.5 rounded-full shrink-0', project.status === 'Active' ? 'bg-success' : 'bg-secondary']"></span>
                  {{ project.status }}
                </span>
              </div>

              <span class="sm:hidden col-start-1 inline-flex w-fit items-center gap-1 h-5 px-2 rounded-full text-[0.65rem] font-bold border"
                :class="project.status === 'Active' ? 'bg-success/10 border-success/20 text-success' : 'bg-secondary/10 border-secondary/20 text-secondary'">
                <span :class="['w-1.5 h-1.5 rounded-full shrink-0', project.status === 'Active' ? 'bg-success' : 'bg-secondary']"></span>
                {{ project.status }}
              </span>

              <div class="hidden sm:flex items-center justify-end gap-2 shrink-0 min-w-0">
                <div class="flex items-center -space-x-1 shrink-0">
                  <MemberAvatar
                    v-for="(member, memberIdx) in visibleMembers(project)"
                    :key="member.userId ?? member.id ?? memberIdx"
                    :name="member.fullName || 'Unknown'"
                    :style="{ zIndex: MAX_VISIBLE_MEMBERS - memberIdx }"
                  />
                  <Avatar
                    v-if="extraMembersCount(project) > 0"
                    :label="`+${extraMembersCount(project)}`"
                    shape="circle"
                    class="bg-secondary text-text-inverse font-bold w-6! h-6! text-[0.65rem]! flex items-center justify-center ring-2 ring-background border border-background"
                    :style="{ zIndex: 0 }"
                  />
                </div>
                <span class="text-xs text-text-muted truncate hidden xl:inline max-w-28">{{ projectMemberNames(project) }}</span>
              </div>

              <div class="flex items-center justify-end shrink-0">
                <span class="hidden sm:inline-flex items-center justify-center gap-1 h-7 min-w-[68px] px-3 rounded-full text-xs font-bold transition-colors"
                  :class="project.id === selectedProjectId ? 'bg-success text-white' : 'bg-primary text-text-inverse group-hover:bg-primary-dark'"
                >
                  {{ project.id === selectedProjectId ? 'Selected' : 'Open' }}
                  <i :class="project.id === selectedProjectId ? 'fa-solid fa-check text-[0.6rem]' : 'fa-solid fa-arrow-right text-[0.6rem]'"></i>
                </span>
                <i class="fa-solid fa-chevron-right text-text-muted/50 text-xs sm:hidden shrink-0"></i>
              </div>
            </button>
          </template>
        </div>

        <div class="border-t border-border shrink-0 bg-background-card">
          <Paginator
            :current-page="page"
            :page-count="totalPages"
            :page-size="pageSize"
            :total-items="totalProjects"
            item-label="projects"
            @update:currentPage="goToPage"
            @first="goToPage(1)"
            @prev="goToPage(page - 1)"
            @next="goToPage(page + 1)"
            @last="goToPage(totalPages)"
          />
        </div>
      </div>
    </div>
  </Page>
</template>

<script setup lang="ts">
import Page from '@/shared/components/layouts/Page.vue';
import Paginator from '@/shared/components/ui/Paginator.vue';
import { computed, onMounted, defineComponent, h, watch, mergeProps } from 'vue';
import Avatar from 'primevue/avatar';
import InputText from 'primevue/inputtext';
import { useProfileStore } from '@/shared/stores/profile';
import { useProjectStore } from '@/shared/stores/project';
import { useUserAvatar } from '@/shared/composables/useUserAvatar';
import router from '@/router';
import type { UiProject, UiProjectMember } from '@/shared/mappers';
import { ProjectRole } from '@/core/types/models/RoleTypes';

definePage({
  meta: {
    title: 'Select a Project',
    requiresProject: false,
  },
});

const profileStore = useProfileStore();
const projectStore = useProjectStore();

const userName = computed(() => profileStore.profile?.name || 'User');
const search = computed({
  get: () => projectStore.search,
  set: (val: string) => projectStore.setSearch(val),
});

const projects = computed(() => projectStore.projects);
const recentProjects = computed(() => projectStore.recentProjects);
const isLoading = computed(() => projectStore.isLoading);
const isError = computed(() => !!projectStore.error);
const errorMessage = computed(() => projectStore.error ?? 'Failed to load projects. Please try again.');
const page = computed(() => projectStore.page);
const pageSize = computed(() => projectStore.pageSize);
const totalProjects = computed(() => projectStore.totalProjects);
const totalPages = computed(() => {
  const total = totalProjects.value;
  const size = pageSize.value;
  if (!size || size <= 0) return 1;
  const pages = Math.ceil(total / size);
  return Math.max(pages, 1);
});

const DEFAULT_PAGE_SIZE = 20;

const showRecentProjects = computed(() =>
  search.value.trim() === '' && page.value === 1 && recentProjects.value.length > 0
);

const MAX_VISIBLE_MEMBERS = 3;

function ownerMembers(project: UiProject): UiProjectMember[] {
  return project.members.filter(member => member.role === ProjectRole.ProjectOwner);
}
function visibleMembers(project: UiProject): UiProjectMember[] {
  return ownerMembers(project).slice(0, MAX_VISIBLE_MEMBERS);
}
function extraMembersCount(project: UiProject): number {
  return Math.max(ownerMembers(project).length - MAX_VISIBLE_MEMBERS, 0);
}
function projectMemberNames(project: UiProject): string {
  const owners = ownerMembers(project);
  const displayedOwners = owners.slice(0, MAX_VISIBLE_MEMBERS);
  if (owners.length === 0) return 'No owners';
  const names = displayedOwners.map(member => {
    const name = (member.fullName ?? '').trim();
    return name.length > 0 ? name : 'Unknown';
  });
  const extra = Math.max(owners.length - displayedOwners.length, 0);
  return extra > 0 ? `${names.join(', ')} +${extra}` : names.join(', ');
}

async function doFetchProjects() {
  await projectStore.fetchRecentProjects();
  await projectStore.fetchProjects({ page: 1, pageSize: DEFAULT_PAGE_SIZE, search: search.value });
}

function refreshCurrentPage(): void {
  const size = pageSize.value || DEFAULT_PAGE_SIZE;
  projectStore.fetchProjects({ page: page.value, pageSize: size, search: search.value });
}
onMounted(() => {
  doFetchProjects();
});

function goToPage(newPage: number) {
  const size = pageSize.value || DEFAULT_PAGE_SIZE;
  const maxPage = Math.max(totalPages.value, 1);
  const clampedPage = Math.min(Math.max(newPage, 1), maxPage);
  if (clampedPage === page.value) return;
  projectStore.fetchProjects({ page: clampedPage, pageSize: size, search: search.value });
}

watch(
  () => search.value,
  (newValue, oldValue) => {
    if (newValue === oldValue) return;
    const size = pageSize.value || DEFAULT_PAGE_SIZE;
    projectStore.fetchProjects({ page: 1, pageSize: size, search: newValue });
  }
);
const selectedProjectId = computed(() => projectStore.selectedProjectId);

async function selectProject(id: string): Promise<void> {
  await projectStore.selectProject(id);
  await router.push({ path: '/asset-hierarchy' });
}

const MemberAvatar = defineComponent({
  name: 'MemberAvatar',
  props: { name: { type: String, required: true } },
  setup(props, { attrs }) {
    const { initials, colorClass } = useUserAvatar(props.name);
    return () =>
      h(
        Avatar,
        mergeProps(
          {
            label: initials.value,
            shape: 'circle',
            class: [
              colorClass.value,
              'w-6! h-6! text-[0.65rem]! font-bold! text-white! ring-2 ring-background border border-background',
            ],
          },
          attrs
        )
      );
  },
});
</script>
